import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { AppDispatch, AppState } from "@/store";
import {
  createBCRequestByJobseekerIds,
  getPackgageById,
  sendInviteForBcRequest,
} from "@/api/backgroundCheck";
import { searchUserByEmail } from "@/api/user";
import { getPackagesAction, setSelectedPackage } from "@/store/slices/packageSlice";
import { useParams, useNavigate } from "react-router-dom";
import { MailPlus, Search, ShieldCheck, ShoppingCart, User } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import EmailDomainPopup from "@/components/auth/EmailDomainPopup";
import { validateAndSuggestEmailDomain } from "@/utils/validation";
import { usePosthog } from "@/hooks/usePosthog";
import { useTranslation } from "react-i18next";
import { hasPermission } from "@/utils/permission";
import { usePermission } from "@/hooks/userPermission";
import { PERMISSIONS } from "@/constants";
import { useAppSelector } from "@/hooks/redux";
import { cn } from "@/lib/utils";
import { VcPageHeader } from "@/components/VerificationChecks/VcChrome";

const isValidEmail = (email: string) =>
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

export default function SendRequestWithoutJobPostPage() {
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { trackEvent } = usePosthog();
  const { allPackages, selectedPackage } = useSelector((state: AppState) => state.packages);
  const [packageDetails, setPackageDetails] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [emailValue, setEmailValue] = useState("");
  // Tri-state: "" = untouched, object = found jobseeker, null = not found.
  const [candidates, setCandidates] = useState<any>("");
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>([]);
  const [suggestedDomain, setSuggestedDomain] = useState<string>("");
  const [showDomainPopup, setShowDomainPopup] = useState<boolean>(false);
  const [isChatLoading, setIsChatLoading] = useState<boolean>(false);
  const [isProceedLoading, setIsProceedLoading] = useState(false);
  const proceedRequestInFlightRef = useRef(false);
  const [isInviteLoading, setIsInviteLoading] = useState(false);
  const inviteRequestInFlightRef = useRef(false);
  const { userData } = useAppSelector((state) => state.auth);
  const { notPermitted } = usePermission();

  useEffect(() => {
    if (
      userData?.user?.type === "employer" &&
      !hasPermission("jobs", userData?.user?.permissions)
    ) {
      notPermitted({ permissionName: PERMISSIONS.verification_checks });
    }
  }, [userData, notPermitted]);

  const validateEmailDomain = (email: string) => {
    const { isValid, suggestedDomain: suggestion } = validateAndSuggestEmailDomain(email);
    if (isValid) {
      setShowDomainPopup(false);
      setSuggestedDomain("");
    } else if (suggestion) {
      setSuggestedDomain(suggestion);
      setShowDomainPopup(true);
    } else {
      setShowDomainPopup(false);
      setSuggestedDomain("");
    }
  };

  const handleCandidateSelect = (candidateId: string) => {
    if (selectedCandidates.includes(candidateId)) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates([candidateId]);
    }
    trackEvent("Candidate Selection Toggled", { candidateId });
  };

  const handleProceed = async () => {
    if (
      userData?.user?.type === "employer" &&
      !hasPermission("jobs", userData?.user?.permissions)
    ) {
      notPermitted({ permissionName: PERMISSIONS.verification_checks });
      return;
    }

    if (!selectedCandidates.length) {
      toast({ title: t("send_requests.toast.select_candidate") });
      return;
    }

    if (proceedRequestInFlightRef.current) {
      return;
    }

    proceedRequestInFlightRef.current = true;
    setIsProceedLoading(true);
    trackEvent("Proceed Button Clicked", { selectedCandidatesCount: selectedCandidates.length });

    const payload = {
      jobseeker_ids: selectedCandidates,
      pkg_id: selectedPackage?.pkg_id || packageDetails?.pkg_id,
    };
    try {
      // V2 contract: a resolved promise IS success — failures always throw,
      // so gating on the (unread) response body would misreport empty results.
      await createBCRequestByJobseekerIds(payload);
      toast({ title: t("employer_bc_pricing.toast.request_created") });
      setSelectedCandidates([]);
      setCandidates("");
      setSearchQuery("");
      trackEvent("Request Created Successfully", { payload });
      navigate("/employer/verification-checks/dashboard");
    } catch (error: any) {
      toast({
        title: error?.response?.data?.error?.message || t("send_requests.toast.unexpected_error"),
      });
      trackEvent("Request Exception", { payload, error: error?.message });
    } finally {
      proceedRequestInFlightRef.current = false;
      setIsProceedLoading(false);
    }
  };

  const handleInvite = async () => {
    if (
      userData?.user?.type === "employer" &&
      !hasPermission("jobs", userData?.user?.permissions)
    ) {
      notPermitted({ permissionName: PERMISSIONS.verification_checks });
      return;
    }

    if (!isValidEmail(emailValue.trim())) {
      toast({ title: t("send_requests.toast.invalid_email") });
      return;
    }

    if (inviteRequestInFlightRef.current) {
      return;
    }

    inviteRequestInFlightRef.current = true;
    setIsInviteLoading(true);
    trackEvent("Invite Button Clicked", { email: emailValue });

    const payload = {
      emails: [emailValue.trim()],
      pkg_id: selectedPackage?.pkg_id || packageDetails?.pkg_id,
    };

    try {
      // A 2xx means the invite went out; failures arrive as thrown 400/404s.
      // Don't gate on the body — a successful re-invite returns an empty list.
      await sendInviteForBcRequest(payload);

      toast({ title: t("send_requests.toast.invite_sent") });
      setEmailValue("");
      setSearchQuery("");
      setCandidates("");
      trackEvent("Invite Sent Successfully", { payload });
    } catch (error: any) {
      toast({
        title: error?.response?.data?.error?.message || t("send_requests.toast.invite_failed"),
      });
      trackEvent("Invite Exception", { payload, error: error?.message });
    } finally {
      inviteRequestInFlightRef.current = false;
      setIsInviteLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!isValidEmail(searchQuery.trim())) {
      toast({ title: t("send_requests.toast.invalid_email") });
      return;
    }
    trackEvent("Search Button Clicked", { query: searchQuery.trim() });
    validateEmailDomain(searchQuery.trim());
    setIsChatLoading(true);
    try {
      const data = await searchUserByEmail(searchQuery.trim());
      if (data && data.type === "job_seeker") {
        setCandidates(data);
        setEmailValue("");
        setIsChatLoading(false);
      } else {
        setCandidates(null);
        setIsChatLoading(false);
      }
      return data;
    } catch {
      setCandidates(null);
      setEmailValue(searchQuery.trim());
      setIsChatLoading(false);
      return null;
    }
  };

  const fetchPackageDetails = async () => {
    try {
      const response = await getPackgageById(id!);
      setPackageDetails(response);
      dispatch(setSelectedPackage(response));
      trackEvent("Package Details Fetched", { packageId: id });
    } catch {
      toast({ title: t("jobseeker_bc_application_form.toast.error_fetching_package_details") });
      trackEvent("Package Details Fetch Error", { packageId: id });
    }
  };

  const handleConfirmEmailDomain = (domain: string) => {
    setShowDomainPopup(false);
    setSuggestedDomain("");
    const email = searchQuery.split("@")[0];
    setSearchQuery(`${email}@${domain}`);
  };

  useEffect(() => {
    fetchPackageDetails();
  }, [id]);

  useEffect(() => {
    if (!allPackages.length) {
      dispatch(getPackagesAction("employer"));
      trackEvent("Packages Fetch Initiated");
    }
  }, [dispatch, allPackages.length]);

  const price = Number(selectedPackage?.price ?? packageDetails?.price ?? 0);
  const quantity = emailValue.length > 0 ? 1 : selectedCandidates.length;
  const inviteMode = emailValue.length > 0;

  return (
    <div className="mx-auto max-w-(--breakpoint-xl) space-y-6 pb-16">
      <VcPageHeader
        title={t("verification_checks.title")}
        subtitle={`${t("apply_request_dialog.title")} · ${t("send_request_without_job_post.title")}`}
      />

      <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
        {/* Cart — first on mobile so the package context leads */}
        <aside className="order-first lg:order-last lg:w-80 lg:shrink-0">
          <div className="space-y-4 lg:sticky lg:top-6">
            <div className="border-border bg-card rounded-2xl border p-5">
              <p className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium tracking-wider uppercase">
                <ShoppingCart className="h-3.5 w-3.5" />
                {t("jobseeker_bc_application_form.summary.title")}
              </p>
              <h3 className="text-foreground mt-1.5 text-base font-semibold">
                {selectedPackage?.name ?? packageDetails?.name}
              </h3>
              <p className="mt-2 flex items-baseline gap-1.5">
                <span className="text-foreground text-2xl font-bold tracking-tight">
                  ${price.toFixed(2)}
                </span>
                <span className="text-muted-foreground text-xs font-medium">/ {t("person")}</span>
              </p>
              <div className="border-border mt-4 space-y-1.5 border-t pt-3 text-sm">
                <div className="text-muted-foreground flex items-center justify-between">
                  <span>
                    {t("send_requests.cart.candidates")} × {quantity}
                  </span>
                  <span className="text-foreground font-semibold tabular-nums">
                    ${(price * quantity).toFixed(2)}
                  </span>
                </div>
              </div>
              {inviteMode ? (
                <Button
                  variant="default"
                  className="mt-4 h-11 w-full rounded-lg"
                  disabled={isInviteLoading}
                  loading={isInviteLoading}
                  onClick={handleInvite}
                >
                  {t("invite")}
                </Button>
              ) : (
                <Button
                  variant="default"
                  className="mt-4 h-11 w-full rounded-lg"
                  disabled={!selectedCandidates.length || isProceedLoading}
                  loading={isProceedLoading}
                  onClick={handleProceed}
                >
                  {t("proceed")}
                </Button>
              )}
            </div>
          </div>
        </aside>

        <div className="min-w-0 flex-1 space-y-4 lg:max-w-2xl">
          <div>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {t("send_request_without_job_post.instructions.select_candidates")}
            </p>
            <div className="mt-3 flex gap-2">
              <div className="relative min-w-0 flex-1">
                <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                <Input
                  type="email"
                  placeholder={t("send_request_without_job_post.candidate.search_placeholder")}
                  className="h-11 rounded-lg pl-9"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                />
              </div>
              <Button
                variant="outline-soft"
                className="h-11 shrink-0 rounded-lg px-6"
                onClick={handleSearch}
              >
                {t("send_request_without_job_post.search_button")}
              </Button>
            </div>
          </div>

          {isChatLoading && (
            <div className="border-border bg-card flex items-center gap-3 rounded-xl border p-4">
              <Skeleton className="h-5 w-5 rounded" />
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          )}

          {/* Found: single-select result */}
          {!isChatLoading && candidates && typeof candidates === "object" && (
            <label
              className={cn(
                "flex cursor-pointer items-center justify-between gap-3 rounded-xl border p-4 transition-colors",
                selectedCandidates.includes(candidates.id)
                  ? "border-brand-accent-border bg-brand-accent"
                  : "border-border bg-card hover:border-brand-accent-border",
              )}
            >
              <div className="flex min-w-0 items-center gap-3">
                <Checkbox
                  checked={selectedCandidates.includes(candidates.id)}
                  onCheckedChange={() => handleCandidateSelect(candidates.id)}
                />
                <Avatar className="h-10 w-10">
                  <AvatarImage src={candidates.photo_thumbnail_url} alt={candidates.full_name} />
                  <AvatarFallback>
                    <User className="bg-brand-accent text-primary h-full w-full rounded-full p-2" />
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <h3 className="text-foreground flex items-center gap-1.5 truncate text-sm font-semibold">
                    {candidates.full_name}
                    {candidates.bc_verified && (
                      <ShieldCheck className="fill-primary h-4 w-4 shrink-0 stroke-white" />
                    )}
                  </h3>
                  <p className="text-muted-foreground truncate text-xs">{candidates.email}</p>
                </div>
              </div>
            </label>
          )}

          {/* Not found: invite lane */}
          {!isChatLoading && candidates === null && (
            <div className="border-border bg-card rounded-2xl border border-dashed p-6">
              <div className="flex items-start gap-3">
                <span className="bg-brand-accent text-primary flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl">
                  <MailPlus className="h-6 w-6" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-foreground text-sm font-semibold">
                    {t("send_request_without_job_post.candidate.user_not_found")}
                  </p>
                  <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed">
                    {t("send_request_without_job_post.candidate.not_found")}
                  </p>
                  <Input
                    type="email"
                    placeholder={t("send_request_without_job_post.candidate.enter_email")}
                    className="mt-3 h-11 rounded-lg"
                    value={emailValue}
                    onChange={(e) => setEmailValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleInvite();
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showDomainPopup && suggestedDomain && (
        <EmailDomainPopup
          domain={suggestedDomain}
          currentEmail={searchQuery}
          onCancel={() => setShowDomainPopup(false)}
          onConfirm={handleConfirmEmailDomain}
        />
      )}
    </div>
  );
}
